import { html } from 'htm/preact';
import { useState, useEffect, useContext, useMemo, useRef } from 'preact/hooks';
import { Select, FormContext } from '../../../bpmn.io/@bpmn-io/form-js';

export const type = 'select';

/**
 * Resuelve un valor en un objeto usando notación de punto.
 * Ej: getByPath({ flujo: { departamento: '1' } }, 'flujo.departamento') → '1'
 */
function getByPath(obj, dotPath) {
  if (!obj || !dotPath) return null;
  const parts = dotPath.split('.');
  let current = obj;
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return null;
    }
    current = current[part];
  }
  return current !== undefined ? current : null;
}

/**
 * Componente de selección con soporte de cascada.
 *
 * Cuando field.cascada === true y field.llaveCascada está definido,
 * filtra las opciones (field.values) mostrando sólo aquellas cuyo
 * `valorCascada` coincida con el valor actual del campo padre.
 *
 * Si no está configurado como cascada, delega al componente Select original.
 */
export function CascadeSelect(props) {
  const { field, onChange, value } = props;
  const { cascada = false, llaveCascada = '', values = [] } = field;

  const contextValue = useContext(FormContext);

  // ── Estado: valor actual del campo padre ───────────────────────────────────
  const [parentValue, setParentValue] = useState(() => {
    if (!cascada || !llaveCascada || !contextValue?.getService) return null;
    try {
      const form = contextValue.getService('form');
      if (!form) return null;
      return getByPath(form._getState().data, llaveCascada);
    } catch (e) {
      return null;
    }
  });

  // Ref para detectar cambios reales vs. primera inicialización
  const prevParentValueRef = useRef(undefined);

  // ── Suscripción a eventos del formulario ──────────────────────────────────
  useEffect(() => {
    if (!cascada || !llaveCascada || !contextValue?.getService) return;

    let form;
    try {
      form = contextValue.getService('form');
    } catch (e) {
      return;
    }
    if (!form) return;

    // Sincronizar valor inicial por si no se capturó en useState
    try {
      setParentValue(getByPath(form._getState().data, llaveCascada));
    } catch (e) { /* ignorar */ }

    const handler = () => {
      try {
        setParentValue(getByPath(form._getState().data, llaveCascada));
      } catch (e) { /* ignorar */ }
    };

    form.on('changed', handler);
    return () => {
      try { form.off('changed', handler); } catch (e) { /* ignorar */ }
    };
  }, [cascada, llaveCascada, contextValue]);

  // ── Limpiar valor de este campo cuando cambia el padre ────────────────────
  useEffect(() => {
    if (!cascada || !llaveCascada) return;

    const isFirstRun = prevParentValueRef.current === undefined;
    const oldValue = prevParentValueRef.current;
    prevParentValueRef.current = parentValue;

    if (!isFirstRun && String(oldValue) !== String(parentValue)) {
      // El padre cambió: limpiar la selección actual de este campo
      onChange({ value: null });
    }
  }, [parentValue, cascada, llaveCascada]);

  // ── Filtrado de opciones ──────────────────────────────────────────────────
  const filteredValues = useMemo(() => {
    if (!cascada || !llaveCascada) return values;
    if (parentValue === null || parentValue === undefined || parentValue === '') return [];
    return values.filter(
      (opt) =>
        opt.valorCascada !== undefined &&
        opt.valorCascada !== null &&
        String(opt.valorCascada) === String(parentValue)
    );
  }, [cascada, llaveCascada, values, parentValue]);

  const filteredField = useMemo(
    () => ({ ...field, values: filteredValues }),
    [field, filteredValues]
  );

  // ── Campo final con searchable inyectado si buscador está activo ─────────
  const finalField = useMemo(() => {
    const base = (cascada && llaveCascada) ? filteredField : field;
    return field.buscador ? { ...base, searchable: true } : base;
  }, [field, filteredField, cascada, llaveCascada]);

  // ── Renderizado ───────────────────────────────────────────────────────────
  return html`<${Select} ...${props} field=${finalField} />`;
}

CascadeSelect.config = {
  ...Select.config,
  type: 'select',
};

CascadeSelect.config = {
  ...Select.config,
  type: 'select',
};
